import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ScrollText, Shield, Filter, User, Calendar, Activity } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import AdminLayout from '../../components/AdminLayout';
import { SidebarProvider } from '../../components/AdminSidebarNew';
import { useAuth } from '../../context/AuthContext';
import { logsApi, usersApi } from '../../lib/api';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const actionLabels = {
  login: 'Inicio de sesión',
  create: 'Creación',
  update: 'Actualización',
  delete: 'Eliminación',
  upload: 'Subida',
  validate: 'Validación',
  status_change: 'Cambio de estado',
  password_change: 'Cambio de contraseña',
  reset_password: 'Restablecimiento de contraseña',
};

const resourceLabels = {
  auth: 'Autenticación',
  event: 'Evento',
  gallery: 'Galería',
  file: 'Archivo',
  ticket: 'Ticket',
  user: 'Usuario',
  role: 'Rol',
  settings: 'Configuración',
};

const LogsPage = () => {
  const { isOwner } = useAuth();
  const [logs, setLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    user_id: 'all',
    action: 'all',
    resource_type: 'all'
  });

  useEffect(() => {
    const fetchData = async () => {
      if (!isOwner) return;
      
      try {
        const params = {};
        if (filters.user_id !== 'all') params.user_id = filters.user_id;
        if (filters.action !== 'all') params.action = filters.action;
        if (filters.resource_type !== 'all') params.resource_type = filters.resource_type;
        params.limit = 100;

        const [logsRes, usersRes] = await Promise.all([
          logsApi.getAll(params),
          usersApi.getAll()
        ]);
        setLogs(logsRes.data);
        setUsers(usersRes.data);
      } catch (error) {
        console.error('Error fetching logs:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [isOwner, filters]);

  if (!isOwner) {
    return (
      <SidebarProvider>
        <AdminLayout>
          <div className="card-dashboard text-center py-16">
            <Shield size={48} className="mx-auto mb-4 text-[#A1A1AA]" />
            <h3 className="text-xl font-black uppercase mb-4">Acceso Restringido</h3>
            <p className="text-[#A1A1AA]">
              Solo el administrador principal puede ver el registro de actividad.
            </p>
          </div>
        </AdminLayout>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider>
      <AdminLayout>
        <div data-testid="logs-page">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="mb-12">
              <h1 className="text-4xl font-black tracking-tighter uppercase mb-2">
                Registro de Actividad
              </h1>
              <p className="text-[#A1A1AA]">
                Historial de acciones sensibles del sistema
              </p>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-4 mb-8">
              <div className="flex items-center gap-2">
                <Filter size={16} className="text-[#A1A1AA]" />
                <span className="text-sm text-[#A1A1AA] uppercase tracking-widest">Filtros:</span>
              </div>
              
              <Select 
                value={filters.user_id} 
                onValueChange={(value) => setFilters(prev => ({ ...prev, user_id: value }))}
              >
                <SelectTrigger className="w-48 bg-transparent border border-[#27272A] rounded-none">
                  <SelectValue placeholder="Usuario" />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border border-[#27272A]">
                  <SelectItem value="all">Todos los usuarios</SelectItem>
                  {users.map(user => (
                    <SelectItem key={user.id} value={user.id}>{user.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select 
                value={filters.action} 
                onValueChange={(value) => setFilters(prev => ({ ...prev, action: value }))}
              >
                <SelectTrigger className="w-48 bg-transparent border border-[#27272A] rounded-none">
                  <SelectValue placeholder="Acción" />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border border-[#27272A]">
                  <SelectItem value="all">Todas las acciones</SelectItem>
                  {Object.entries(actionLabels).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select 
                value={filters.resource_type} 
                onValueChange={(value) => setFilters(prev => ({ ...prev, resource_type: value }))}
              >
                <SelectTrigger className="w-48 bg-transparent border border-[#27272A] rounded-none">
                  <SelectValue placeholder="Recurso" />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border border-[#27272A]">
                  <SelectItem value="all">Todos los recursos</SelectItem>
                  {Object.entries(resourceLabels).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Logs List */}
            {loading ? (
              <div className="card-dashboard animate-pulse">
                <div className="space-y-4">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="h-16 bg-[#1A1A1A]" />
                  ))}
                </div>
              </div>
            ) : logs.length === 0 ? (
              <div className="card-dashboard text-center py-16">
                <ScrollText size={48} className="mx-auto mb-4 text-[#A1A1AA]" />
                <h3 className="text-xl font-black uppercase mb-4">Sin registros</h3>
                <p className="text-[#A1A1AA]">No hay actividad registrada con estos filtros.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {logs.map((log) => (
                  <motion.div
                    key={log.id}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="card-dashboard flex items-center gap-4 py-4"
                  >
                    <div className="w-10 h-10 bg-[#27272A] flex items-center justify-center flex-shrink-0">
                      <Activity size={18} className="text-[#A1A1AA]" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-bold">{log.username}</span>
                        <span className="text-[#A1A1AA]">•</span>
                        <span className="text-[#A1A1AA]">
                          {actionLabels[log.action] || log.action}
                        </span>
                        <span className="px-2 py-0.5 text-xs uppercase font-mono bg-[#27272A] text-[#A1A1AA]">
                          {resourceLabels[log.resource_type] || log.resource_type}
                        </span>
                      </div>
                      {log.details && (
                        <p className="text-sm text-[#52525B] truncate">
                          {JSON.stringify(log.details)}
                        </p>
                      )}
                    </div>

                    <div className="text-right flex-shrink-0">
                      <p className="font-mono text-sm text-[#A1A1AA]">
                        {format(new Date(log.timestamp), 'd MMM, HH:mm:ss', { locale: es })}
                      </p>
                      {log.ip_address && (
                        <p className="text-xs text-[#52525B] font-mono">{log.ip_address}</p>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>
        </div>
        </AdminLayout>
      </SidebarProvider>
  );
};

export default LogsPage;
